import { supabase } from '../lib/supabase';
import { getChatCompletion } from './aiIntegrations/chatCompletion';

const AI_BACKEND_URL = supabase?.functions?.url ? `${supabase?.functions?.url}` : '';
const LAMBDA_CHAT_URL = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;

/**
 * 🔒 SECURE Grow Guardian AI Service
 *
 * ARCHITECTURE: AI requests route through Supabase Edge Functions (server-side)
 *
 * SECURITY GUARANTEE:
 * - React frontend NEVER calls AI APIs directly
 * - ALL API keys stored ONLY in Supabase Edge Function environment variables
 * - Zero keys in frontend code or browser environment
 * - Authenticated requests via JWT tokens
 *
 * REQUEST FLOW:
 * User Browser → growGuardianAIService.js → Supabase Edge Function → AI API
 *              ↑ (JWT Auth)                 ↑ (Server-side key only)
 */

/**
 * Get current user's access token for authentication
 */
const getAccessToken = async () => {
  const { data: { session }, error } = await supabase?.auth?.getSession();
  if (error || !session) {
    throw new Error('Not authenticated. Please sign in to use AI features.');
  }
  return session?.access_token;
};

/**
 * Chat Assistant - Text-based Grow Guardian assistant
 * Routes through AWS Lambda (chat-completion) — API key is server-side only
 */
export const chatAssistant = async (prompt, context = {}) => {
  try {
    let systemPrompt = buildSystemPrompt(context);

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: prompt },
    ];

    const response = await getChatCompletion('GEMINI', 'gemini/gemini-2.5-flash', messages, {
      max_tokens: 1024,
    });

    // Check if Lambda returned an error object
    if (response?.error) {
      throw new Error(response.error);
    }

    // Lambda returns the raw SDK response; extract the text content
    const answer =
      response?.choices?.[0]?.message?.content ||
      response?.content?.[0]?.text ||
      response?.answer ||
      response?.candidates?.[0]?.content?.parts?.[0]?.text ||
      null;

    if (!answer) {
      throw new Error('No response generated from AI service');
    }

    return { success: true, answer, meta: { model: 'gemini/gemini-2.5-flash' } };
  } catch (error) {
    console.error('Error calling chat assistant:', error);
    throw error;
  }
};

/**
 * Build system prompt from context object
 */
function buildSystemPrompt(context = {}) {
  let systemPrompt = `You are Grow Guardian AI, an expert IPM (Integrated Pest Management) assistant specializing in cannabis cultivation. 
You provide detailed, actionable advice on pest control, disease management, biological controls, spray rotations, and environmental strategies.
Always prioritize safety, compliance with regulations, and sustainable practices.`;

  if (context && Object.keys(context)?.length > 0) {
    systemPrompt += `\n\nCurrent Context:`;
    if (context?.cropType) systemPrompt += `\n- Crop Type: ${context?.cropType}`;
    if (context?.growthStage) systemPrompt += `\n- Growth Stage: ${context?.growthStage}`;
    if (context?.flowerWeek) systemPrompt += `\n- Flower Week: ${context?.flowerWeek}`;
    if (context?.medium) systemPrompt += `\n- Growing Medium: ${context?.medium}`;
    if (context?.roomId) systemPrompt += `\n- Room: ${context?.roomId}`;
    if (context?.pressure) {
      systemPrompt += `\n- Pest Pressure Levels:`;
      Object.entries(context?.pressure)?.forEach(([pest, level]) => {
        systemPrompt += `\n  * ${pest}: ${level}/5`;
      });
    }
  }

  return systemPrompt;
}

/**
 * Pest Detection - AI analysis of leaf/canopy/sticky-card images
 * @param {string} imageUrl - Secure URL of the uploaded image
 * @param {Object} context - Optional context (crop, stage, room, notes)
 * @returns {Promise<Object>} Pest detection results with confidence scores
 */
export const detectPestsFromImage = async (imageUrl, context = {}) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${AI_BACKEND_URL}/pest-detect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ imageUrl, context }),
    });

    if (!response?.ok) {
      const errorData = await response?.json()?.catch(() => ({}));
      throw new Error(errorData?.error || `Backend error: ${response?.status}`);
    }

    const data = await response?.json();
    
    if (!data?.success) {
      throw new Error(data?.error || 'Pest detection error');
    }

    return data;
  } catch (error) {
    console.error('Error detecting pests:', error);
    throw error;
  }
};

/**
 * Deficiency Detection - Identify nutrient deficiencies from leaf photos
 * @param {string} imageUrl - Secure URL of the uploaded leaf image
 * @param {Object} context - Optional context (medium, EC, pH, stage, notes)
 * @returns {Promise<Object>} Deficiency diagnosis with recommendations
 */
export const detectDeficiencyFromImage = async (imageUrl, context = {}) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${AI_BACKEND_URL}/deficiency-detect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ imageUrl, context }),
    });

    if (!response?.ok) {
      const errorData = await response?.json()?.catch(() => ({}));
      throw new Error(errorData?.error || `Backend error: ${response?.status}`);
    }

    const data = await response?.json();
    
    if (!data?.success) {
      throw new Error(data?.error || 'Deficiency detection error');
    }

    return data;
  } catch (error) {
    console.error('Error detecting deficiency:', error);
    throw error;
  }
};

/**
 * IPM Rotation Generator - Generate week-by-week IPM rotation plan
 * @param {Object} input - Rotation parameters (crop, stage, pressure, constraints)
 * @returns {Promise<Object>} Complete IPM rotation schedule
 */
export const generateIPMRotation = async (input) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${AI_BACKEND_URL}/ipm-rotation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response?.ok) {
      const errorData = await response?.json()?.catch(() => ({}));
      throw new Error(errorData?.error || `Backend error: ${response?.status}`);
    }

    const data = await response?.json();
    
    if (!data?.success) {
      throw new Error(data?.error || 'IPM rotation generation error');
    }

    return data;
  } catch (error) {
    console.error('Error generating IPM rotation:', error);
    throw error;
  }
};

/**
 * Upload image to Supabase Storage and get secure URL
 * @param {File} file - Image file to upload
 * @param {string} bucket - Storage bucket name (default: 'ai-uploads')
 * @returns {Promise<string>} Secure URL of uploaded image
 */
export const uploadImageForAI = async (file, bucket = 'ai-uploads') => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${user?.id}/${Date.now()}.${fileExt}`;

    const { data, error } = await supabase?.storage?.from(bucket)?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase?.storage?.from(bucket)?.getPublicUrl(data?.path);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Export service object for easy import
export default {
  chatAssistant,
  detectPestsFromImage,
  detectDeficiencyFromImage,
  generateIPMRotation,
  uploadImageForAI,
};